/**
 * Encuentra la casilla más cercana a una posición dada
 * @param {number} x - Coordenada X relativa al grid
 * @param {number} y - Coordenada Y relativa al grid
 * @param {DOMRect} gridRect - Rectángulo del grid
 * @param {number} gridSize - Número de casillas por fila/columna
 * @param {Array} tiles - Array de casillas
 * @returns {Object|null} La casilla más cercana o null si no se encuentra
 */
export function findClosestTile(x, y, gridRect, gridSize, tiles) {
  if (!gridRect || !gridSize || !tiles || !isFinite(x) || !isFinite(y)) {
    
    return null;
  }

  if (!isFinite(gridRect.width) || !isFinite(gridRect.height) || 
      gridRect.width <= 0 || gridRect.height <= 0 || gridSize <= 0) {
    
    return null;
  }

  const tileWidth = gridRect.width / gridSize;
  const tileHeight = gridRect.height / gridSize;
  
  if (!isFinite(tileWidth) || !isFinite(tileHeight) || tileWidth <= 0 || tileHeight <= 0) {
    
    return null;
  }
  
  const tileX = Math.round(x / tileWidth);
  const tileY = Math.round(y / tileHeight);
  
  if (!isFinite(tileX) || !isFinite(tileY)) {
    
    return null;
  }
  
  const clampedX = Math.max(0, Math.min(gridSize - 1, tileX));
  const clampedY = Math.max(0, Math.min(gridSize - 1, tileY));
  
  const tileId = clampedY * gridSize + clampedX;
  
  if (tileId >= 0 && tileId < tiles.length && isFinite(tileId)) {
    return tiles[tileId];
  }
  
  
  return null;
}

/**
 * Calcula la distancia euclidiana entre dos puntos
 * @param {number} x1 - Coordenada X del primer punto
 * @param {number} y1 - Coordenada Y del primer punto
 * @param {number} x2 - Coordenada X del segundo punto
 * @param {number} y2 - Coordenada Y del segundo punto
 * @returns {number} Distancia euclidiana
 */
export function euclideanDistance(x1, y1, x2, y2) {
  if (!isFinite(x1) || !isFinite(y1) || !isFinite(x2) || !isFinite(y2)) {
    return Infinity; 
  }

  const dx = x2 - x1;
  const dy = y2 - y1;
  const distance = Math.sqrt(dx * dx + dy * dy);
  
  if (!isFinite(distance)) {
    return Infinity;
  }
  
  return distance;
}

/**
 * Encuentra la casilla más cercana usando fuerza bruta (para casos complejos)
 * @param {number} x - Coordenada X relativa al grid
 * @param {number} y - Coordenada Y relativa al grid
 * @param {DOMRect} gridRect - Rectángulo del grid
 * @param {number} gridSize - Número de casillas por fila/columna
 * @param {Array} tiles - Array de casillas
 * @param {Object} tileRefs - Referencias a los elementos DOM de las casillas
 * @returns {Object|null} La casilla más cercana o null si no se encuentra
 */
export function findClosestTileBruteForce(x, y, gridRect, gridSize, tiles, tileRefs) {
  if (!tiles || !tileRefs || !tileRefs.current || !isFinite(x) || !isFinite(y)) {
    return null;
  }
  
  if (!gridRect || !isFinite(gridRect.left) || !isFinite(gridRect.top)) {
    return null;
  }
  
  let closestTile = null;
  let minDistance = Infinity;
  
  tiles.forEach(tile => {
    if (!tile || !isFinite(tile.id)) return;
    
    const tileElement = tileRefs.current[tile.id];
    if (!tileElement) return;
    
    const tileRect = tileElement.getBoundingClientRect();
    if (!tileRect || !isFinite(tileRect.left) || !isFinite(tileRect.top) || 
        !isFinite(tileRect.width) || !isFinite(tileRect.height)) {
      return;
    }
    
    const gridRectAbs = {
      left: gridRect.left || 0,
      top: gridRect.top || 0
    };
    
    const tileCenterX = (tileRect.left + tileRect.width / 2) - gridRectAbs.left;
    const tileCenterY = (tileRect.top + tileRect.height / 2) - gridRectAbs.top;
    
    if (!isFinite(tileCenterX) || !isFinite(tileCenterY)) {
      return;
    }
    
    const distance = euclideanDistance(x, y, tileCenterX, tileCenterY);
    
    if (isFinite(distance) && distance < minDistance) {
      minDistance = distance;
      closestTile = tile;
    }
  });
  
  return closestTile;
}
